/**
 * Dialog para adicionar/editar um item de entrada (salva direto no banco)
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { ItemEntrada } from "@/types/entrada";
import { entradaAdapter, productsAdapter } from "@/data";
import { toast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/utils";
import { MarcaCombobox } from "./MarcaCombobox";

interface EntradaItemDialogRefatoradoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entradaId: string;
  item: ItemEntrada | null;
  onSuccess: () => void;
}

const itemSchema = z.object({
  produto_id: z.string().min(1, "Produto é obrigatório"),
  produto_nome: z.string().min(1, "Nome do produto é obrigatório"),
  quantidade_compra: z.number().min(0.001, "Quantidade deve ser maior que 0"),
  custo_unitario_compra: z.number().min(0, "Custo unitário deve ser maior ou igual a 0"),
  outras_despesas_item: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().nonnegative().optional()
  ),
  tipo_embalagem: z.enum(['Unidade', 'Pacote', 'Caixa', 'Lata', 'Outro'], {
    required_error: "Tipo de embalagem é obrigatório"
  }),
  peso_individual_g: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().positive("Peso deve ser maior que zero").optional()
  ),
  volume_individual_ml: z.preprocess(
    (val) => {
      if (val === "" || val === null || val === undefined) return undefined;
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().positive("Volume deve ser maior que zero").optional()
  ),
  lote: z.string().optional(),
  validade: z.string().optional(),
  localizacao: z.string().optional(),
  observacao_item: z.string().optional(),
  marca_id: z.string().uuid().optional(),
  forma_pagamento: z.enum([
    'credito_vista',
    'credito_parcelado',
    'dinheiro',
    'debito',
    'pix',
    'boleto',
    'cheque'
  ]).optional().nullable(),
});

type ItemFormValues = z.infer<typeof itemSchema>;

export function EntradaItemDialogRefatorado({
  open,
  onOpenChange,
  entradaId,
  item,
  onSuccess,
}: EntradaItemDialogRefatoradoProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const form = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      produto_id: "",
      produto_nome: "",
      quantidade_compra: 1,
      custo_unitario_compra: 0,
      outras_despesas_item: undefined,
      tipo_embalagem: "Unidade",
      peso_individual_g: undefined,
      volume_individual_ml: undefined,
      lote: "",
      validade: "",
      localizacao: "",
      observacao_item: "",
      marca_id: "",
      forma_pagamento: null,
    },
  });

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open]);

  useEffect(() => {
    if (item && open) {
      form.reset({
        produto_id: item.produto_id,
        produto_nome: item.produto_nome,
        quantidade_compra: item.quantidade_compra,
        custo_unitario_compra: item.custo_unitario_compra,
        outras_despesas_item: item.outras_despesas_item || undefined,
        tipo_embalagem: item.tipo_embalagem,
        peso_individual_g: item.peso_individual_g || undefined,
        volume_individual_ml: item.volume_individual_ml || undefined,
        lote: item.lote || "",
        validade: item.validade || "",
        localizacao: item.localizacao || "",
        observacao_item: item.observacao_item || "",
        marca_id: item.marca_id || "",
        forma_pagamento: item.forma_pagamento || null,
      });
      loadProductById(item.produto_id);
    } else if (!item && open) {
      form.reset({
        produto_id: "",
        produto_nome: "",
        quantidade_compra: 1,
        custo_unitario_compra: 0,
        outras_despesas_item: undefined,
        tipo_embalagem: "Unidade",
        peso_individual_g: undefined,
        volume_individual_ml: undefined,
        lote: "",
        validade: "",
        localizacao: "",
        observacao_item: "",
        marca_id: "",
        forma_pagamento: null,
      });
      setSelectedProduct(null);
    }
  }, [item, open]);

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      const result = await productsAdapter.list();
      setProducts(result.items);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos",
        variant: "destructive",
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadProductById = async (productId: string) => {
    try {
      const product = await productsAdapter.get(productId);
      setSelectedProduct(product);
    } catch (error) {
      console.error("Erro ao carregar produto:", error);
    }
  };

  const handleProductChange = async (productId: string) => {
    form.setValue("produto_id", productId);
    const product = await productsAdapter.get(productId);
    setSelectedProduct(product);
    form.setValue("produto_nome", product?.nome || "");
  };

  const watchQuantidade = form.watch("quantidade_compra");
  const watchCusto = form.watch("custo_unitario_compra");
  const watchOutrasDespesas = form.watch("outras_despesas_item");

  const outrasDespesas = watchOutrasDespesas || 0;
  const custoTotal = watchCusto + outrasDespesas;
  const subtotal = watchQuantidade * custoTotal;

  const onSubmit = async (data: ItemFormValues) => {
    try {
      setIsSubmitting(true);

      const itemData = {
        produto_id: data.produto_id,
        produto_nome: data.produto_nome,
        quantidade_compra: data.quantidade_compra,
        custo_unitario_compra: data.custo_unitario_compra,
        outras_despesas_item: data.outras_despesas_item || null,
        tipo_embalagem: data.tipo_embalagem,
        peso_individual_g: data.peso_individual_g || null,
        volume_individual_ml: data.volume_individual_ml || null,
        lote: data.lote || null,
        validade: data.validade || null,
        localizacao: data.localizacao || null,
        observacao_item: data.observacao_item || null,
        marca_id: data.marca_id || null,
        forma_pagamento: data.forma_pagamento || null,
      };

      if (item?.id) {
        await entradaAdapter.updateItem(item.id, itemData);
        toast({
          title: "Item atualizado",
          description: "Item atualizado com sucesso.",
        });
      } else {
        await entradaAdapter.createItem(entradaId, itemData);
        toast({
          title: "Item adicionado",
          description: "Item adicionado à entrada com sucesso.",
        });
      }

      onSuccess();
    } catch (error: any) {
      toast({
        title: "Erro ao salvar item",
        description: error?.message || "Não foi possível salvar o item",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar Item" : "Adicionar Item"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Produto */}
          <div className="space-y-2">
            <Label htmlFor="produto_id">Produto *</Label>
            <Select
              value={form.watch("produto_id")}
              onValueChange={handleProductChange}
              disabled={loadingProducts}
            >
              <SelectTrigger className={form.formState.errors.produto_id ? "border-destructive" : ""}>
                <SelectValue placeholder="Selecione um produto" />
              </SelectTrigger>
              <SelectContent>
                {products.map((product) => (
                  <SelectItem key={product.id_produto} value={product.id_produto}>
                    {product.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.produto_id && (
              <p className="text-sm text-destructive">{form.formState.errors.produto_id.message}</p>
            )}
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantidade_compra">Quantidade *</Label>
              <Input
                id="quantidade_compra"
                type="number"
                step="0.001"
                {...form.register("quantidade_compra", { valueAsNumber: true })}
                className={form.formState.errors.quantidade_compra ? "border-destructive" : ""}
              />
              {form.formState.errors.quantidade_compra && (
                <p className="text-sm text-destructive">{form.formState.errors.quantidade_compra.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="custo_unitario_compra">Custo Unitário *</Label>
              <Input
                id="custo_unitario_compra"
                type="number"
                step="0.01"
                {...form.register("custo_unitario_compra", { valueAsNumber: true })}
                className={form.formState.errors.custo_unitario_compra ? "border-destructive" : ""}
              />
              {form.formState.errors.custo_unitario_compra && (
                <p className="text-sm text-destructive">{form.formState.errors.custo_unitario_compra.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="outras_despesas_item">Outras Despesas</Label>
              <Input
                id="outras_despesas_item"
                type="number"
                step="0.01"
                {...form.register("outras_despesas_item", { valueAsNumber: true })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Subtotal</Label>
              <div className="h-10 px-3 py-2 border rounded-md bg-muted font-medium">
                {formatCurrency(subtotal)}
              </div>
            </div>
          </div>

          {/* Embalagem */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tipo_embalagem">Tipo de Embalagem *</Label>
              <Select
                value={form.watch("tipo_embalagem")}
                onValueChange={(value: any) => form.setValue("tipo_embalagem", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Unidade">Unidade</SelectItem>
                  <SelectItem value="Pacote">Pacote</SelectItem>
                  <SelectItem value="Caixa">Caixa</SelectItem>
                  <SelectItem value="Lata">Lata</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="peso_individual_g">Peso (g)</Label>
              <Input
                id="peso_individual_g"
                type="number"
                step="0.01"
                {...form.register("peso_individual_g", { valueAsNumber: true })}
                placeholder="Ex: 500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="volume_individual_ml">Volume (ml)</Label>
              <Input
                id="volume_individual_ml"
                type="number"
                step="0.01"
                {...form.register("volume_individual_ml", { valueAsNumber: true })}
                placeholder="Ex: 1000"
              />
            </div>
          </div>

          {/* Rastreabilidade */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lote">Lote</Label>
              <Input
                id="lote"
                {...form.register("lote")}
                placeholder="Ex: L123456"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="validade">Validade</Label>
              <Input
                id="validade"
                type="date"
                {...form.register("validade")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="localizacao">Localização</Label>
              <Input
                id="localizacao"
                {...form.register("localizacao")}
                placeholder="Ex: Prateleira A1"
              />
            </div>
          </div>

          {/* Outros */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="marca_id">Marca</Label>
              <MarcaCombobox
                value={form.watch("marca_id") || ""}
                onValueChange={(value) => form.setValue("marca_id", value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
              <Select
                value={form.watch("forma_pagamento") || ""}
                onValueChange={(value: any) => form.setValue("forma_pagamento", value || null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credito_vista">Crédito à Vista</SelectItem>
                  <SelectItem value="credito_parcelado">Crédito Parcelado</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="debito">Débito</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacao_item">Observação</Label>
            <Textarea
              id="observacao_item"
              {...form.register("observacao_item")}
              placeholder="Observações sobre este item"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {item ? "Atualizar Item" : "Adicionar Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
